import type { EnvironmentId, ScopedProjectRef } from "@afar-research/contracts";
import { buildProjectGroups, type ProjectGroupingSettings } from "./logicalProject";
import type { SidebarProjectGroupingMode } from "@afar-research/contracts/settings";
import type { Project } from "./types";

/** How each grouping rule reads wherever it is offered or explained. */
export const PROJECT_GROUPING_MODE_LABELS: Record<SidebarProjectGroupingMode, string> = {
  repository: "Group by repository",
  repository_path: "Group by repository path",
  separate: "Keep separate",
};

export interface SidebarProjectGroupMember extends Project {
  physicalProjectKey: string;
  environmentLabel: string | null;
}

export interface SidebarProjectSnapshot extends Project {
  projectKey: string;
  displayName: string;
  connectionLabel: string;
  groupedProjectCount: number;
  memberProjects: readonly SidebarProjectGroupMember[];
  memberProjectRefs: readonly ScopedProjectRef[];
}

/** A disconnected Environment keeps its projects in Settings, but not in the sidebar. */
export function connectedSidebarProjects(
  projects: ReadonlyArray<Project>,
  connectedEnvironmentIds: ReadonlySet<EnvironmentId>,
): Project[] {
  return projects.filter((project) => connectedEnvironmentIds.has(project.environmentId));
}

export function buildPhysicalToLogicalProjectKeyMap(input: {
  projects: ReadonlyArray<Project>;
  settings: ProjectGroupingSettings;
  primaryEnvironmentId: EnvironmentId | null;
}): Map<string, string> {
  const mapping = new Map<string, string>();
  const groups = buildProjectGroups({
    projects: input.projects,
    settings: input.settings,
    preferredEnvironmentId: input.primaryEnvironmentId,
  });
  for (const group of groups) {
    for (const member of group.members) {
      mapping.set(member.physicalProjectKey, group.key);
    }
  }
  return mapping;
}

export function buildSidebarProjectSnapshots(input: {
  projects: ReadonlyArray<Project>;
  settings: ProjectGroupingSettings;
  resolveEnvironmentLabel: (environmentId: EnvironmentId) => string | null;
  primaryEnvironmentId: EnvironmentId | null;
}): SidebarProjectSnapshot[] {
  return buildProjectGroups({
    projects: input.projects,
    settings: input.settings,
    preferredEnvironmentId: input.primaryEnvironmentId,
  }).map((group): SidebarProjectSnapshot => {
    const members = group.members.map(
      ({ physicalProjectKey, project }): SidebarProjectGroupMember => ({
        ...project,
        physicalProjectKey,
        environmentLabel: input.resolveEnvironmentLabel(project.environmentId),
      }),
    );
    const representative =
      members.find(
        (member) =>
          member.environmentId === group.representative.environmentId &&
          member.id === group.representative.id,
      ) ?? members[0]!;

    const connectionLabels = [
      ...new Set(
        members.flatMap((member) => (member.environmentLabel ? [member.environmentLabel] : [])),
      ),
    ];

    return {
      ...representative,
      projectKey: group.key,
      displayName: group.label,
      connectionLabel:
        connectionLabels.length > 0 ? connectionLabels.join(" + ") : representative.environmentId,
      groupedProjectCount: members.length,
      memberProjects: members,
      memberProjectRefs: group.memberProjectRefs,
    };
  });
}
